import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";

/**
 * WHO IS THIS PRICE FOR? — the choice itself, with no dialog around it.
 *
 * Publishing a price change used to re-price every active subscription on the
 * plan, silently. It no longer does: the new price is a new VERSION, the old
 * one stays and keeps serving the customers on it, and this is where the admin
 * says who moves.
 *
 *   New customers only   (the default) — nobody on the plan today moves.
 *   All active customers — everyone, each from THEIR OWN next renewal. Never
 *                          mid-cycle, never prorated.
 *   Pick customers       — a searchable list of the active subscriptions.
 *
 * The date shown per customer is not invented here: the server computes it as
 * max(next renewal, price lock expiry, now), so a customer whose price is
 * locked past their next renewal moves at the LATER date. That is the whole
 * point of a price validity lock, so the lock is shown beside the date it
 * produced. A subscription with a pinned price cannot be picked at all.
 *
 * It lives apart from the dialog because it is needed in two shapes: inline,
 * inside the Admin Products confirmation (one dialog, not two — an admin
 * should see the warning and the choice together), and wrapped in a dialog for
 * the vendor approval screen.
 *
 * Reports upward as `{ mode, subscription_ids, note }` on every change, so the
 * parent can hand it straight to the publish.
 */

const MODES = [
  {
    value: "new_only",
    label: "New customers only",
    hint: "Nobody on this plan today changes price.",
  },
  {
    value: "all",
    label: "All active customers",
    hint: "Each moves at their own next renewal — never mid-cycle, no proration.",
  },
  {
    value: "pick",
    label: "Pick customers",
    hint: "Choose exactly who moves.",
  },
];

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

export default function PriceRolloutChoice({
  /** The offers whose price this save changes. Candidates are loaded per id. */
  priceIds = [],
  busy = false,
  /** Called with { mode, subscription_ids, note } whenever the choice moves. */
  onChange,
  /** Called with true/false — false while "Pick" has nobody selected. */
  onValidityChange,
}) {
  const [mode, setMode] = useState("new_only");
  const [note, setNote] = useState("");
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(true);

  // `priceIds` is passed inline by the callers — (plans || []).map(...) — so it
  // is a NEW ARRAY on every parent render. Depending on the array itself made
  // the loader a new function every render, the effect run every render, and
  // that set state and re-rendered: an infinite fetch loop. Depend on the
  // VALUE, not the identity.
  const idsKey = (priceIds || []).filter(Boolean).join(",");

  const load = useCallback(async () => {
    const ids = idsKey ? idsKey.split(",") : [];
    if (!ids.length) {
      setCandidates([]);
      return;
    }
    setLoading(true);
    try {
      const all = [];
      let anyEnabled = false;
      for (const id of ids) {
        const res = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/admin/catalog/price-rollouts/candidates?priceId=${id}`,
          { credentials: "include" },
        );
        const data = await res.json();
        if (data?.enabled) anyEnabled = true;
        (data?.data || []).forEach((row) => all.push({ ...row, price_id: id }));
      }
      setEnabled(anyEnabled);
      setCandidates(all);
    } catch (e) {
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [idsKey]);

  useEffect(() => {
    load();
  }, [load]);

  const movable = useMemo(
    () => candidates.filter((c) => !c.price_pinned),
    [candidates],
  );
  const pinnedCount = candidates.length - movable.length;

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return movable;
    return movable.filter((c) =>
      [c.customer, c.plan, c.product, String(c.subscription_id), String(c.work_id)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [movable, search]);

  const toggle = (id) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Report the choice up on every change, so the parent never has to reach in.
  useEffect(() => {
    onChange?.({
      mode,
      subscription_ids: mode === "pick" ? [...picked] : [],
      note: note.trim() || null,
    });
    onValidityChange?.(mode !== "pick" || picked.size > 0);
    // `onChange`/`onValidityChange` are usually inline arrows; depending on
    // them would reintroduce the render loop this component was fixed for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, picked, note]);

  return (
    <div>
        <p style={{ marginTop: 0, fontSize: 13, color: "#555" }}>
          The new price becomes a new version. The current version stays and
          keeps serving everyone who is not moved.
        </p>

        {!enabled && (
          <p style={{ fontSize: 13, color: "#a15c00" }}>
            Price versions are not migrated on this environment yet, so this
            publish will behave exactly as it does today. The choice below is
            recorded but has no effect until the migration has run.
          </p>
        )}

        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          {MODES.map((m) => (
            <div key={m.value}>
              <FormControlLabel
                value={m.value}
                control={<Radio size="small" />}
                label={m.label}
                disabled={busy}
              />
              <div style={{ marginLeft: 32, marginTop: -6, fontSize: 12, color: "#777" }}>
                {m.hint}
              </div>
            </div>
          ))}
        </RadioGroup>

        {mode !== "new_only" && (
          <div style={{ marginTop: 12 }}>
            {loading ? (
              <div style={{ padding: 16, textAlign: "center" }}>
                <CircularProgress size={20} />
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 8,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search customer, plan or subscription id"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ flex: 1 }}
                    disabled={mode !== "pick"}
                  />
                  <Chip
                    size="small"
                    label={`${movable.length} active`}
                    variant="outlined"
                  />
                  {pinnedCount > 0 && (
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={`${pinnedCount} pinned — never moved`}
                    />
                  )}
                </div>

                <div style={{ maxHeight: 320, overflow: "auto" }}>
                  <table style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: "#666" }}>
                        <th style={{ width: 36 }} />
                        <th>Customer</th>
                        <th>Plan</th>
                        <th>Seats</th>
                        <th>Price locked until</th>
                        <th>Next renewal</th>
                        <th>Takes effect</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((c) => (
                        <tr key={c.subscription_id} style={{ borderTop: "1px solid #eee" }}>
                          <td>
                            <Checkbox
                              size="small"
                              disabled={mode !== "pick" || busy}
                              checked={mode === "all" || picked.has(c.subscription_id)}
                              onChange={() => toggle(c.subscription_id)}
                            />
                          </td>
                          <td>{c.customer || `workspace #${c.work_id ?? "?"}`}</td>
                          <td>{c.plan || c.product || "—"}</td>
                          <td>{c.licences ?? "—"}</td>
                          <td>{fmtDate(c.price_locked_until)}</td>
                          <td>{fmtDate(c.next_renewal)}</td>
                          <td>
                            <strong>{fmtDate(c.would_take_effect_at)}</strong>
                            {c.already_scheduled ? (
                              <Chip
                                size="small"
                                style={{ marginLeft: 6 }}
                                label="already scheduled"
                                variant="outlined"
                              />
                            ) : null}
                          </td>
                        </tr>
                      ))}
                      {!shown.length && (
                        <tr>
                          <td colSpan={7} style={{ padding: 12, color: "#777" }}>
                            No active customers on this plan.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p style={{ fontSize: 11.5, color: "#777", marginBottom: 0 }}>
                  A customer whose price is locked past their next renewal moves
                  on the later date — the lock is honoured, never cut short.
                </p>
              </>
            )}
          </div>
        )}

        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          style={{ marginTop: 12 }}
          label="Note (optional) — kept on the version and on every scheduled change"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={busy}
        />
    </div>
  );
}
